import UploadStructureHtml from "./UploadStructureHtml.js";
import FileDropAreaCmp from "../file-drop/FileDropAreaCmp.js";
import ClipboardPasteEventHandler from "../../js/util/ClipboardPasteEventHandler.js";
import BaseDialogCmp from "../BaseDialog/BaseDialogCmp.js";

export default class UploadStructureCmp {
    #html = new UploadStructureHtml();
    #fileDropAreaCmp = new FileDropAreaCmp();
    #clipboardPasteEventHandler = new ClipboardPasteEventHandler();
    #zoomedStructurePopup = new BaseDialogCmp({cssClass: 'zoomed-svg', btnCancel: null});
    /** @type {SubstanceRepository} */
    #substanceRepository;
    /** @type {StructureSvgLoader} */
    #structureSvgLoader;
    #callbacks = {
        onBeforeProcessingContentToUpload: [],
        onStructureUploadStarted: [],
        onStructureUploaded: [],
        onStructureUploadError: [],
        onImportClicked: [],
    };
    /** @type {boolean} */
    #disabled;

    /**
     * @param {SubstanceRepository} substanceRepository
     * @param {StructureSvgLoader} structureSvgLoader
     */
    constructor(substanceRepository, structureSvgLoader) {
        this.#substanceRepository = substanceRepository;
        this.#structureSvgLoader = structureSvgLoader;
    }

    /** @param {HTMLElement} rootEl */
    init(rootEl) {
        this.#html.init(rootEl);
        this.#fileDropAreaCmp.init(rootEl, {showOverlay: false});
        this.#clipboardPasteEventHandler.init(rootEl);
        this.#setEventListeners();
    }

    onBeforeProcessingContentToUpload(callback) {
        this.#callbacks.onBeforeProcessingContentToUpload.push(callback);
    }

    onStructureUploadStarted(callback) {
        this.#callbacks.onStructureUploadStarted.push(callback);
    }

    onStructureUploaded(callback) {
        this.#callbacks.onStructureUploaded.push(callback);
    }

    onStructureUploadError(callback) {
        this.#callbacks.onStructureUploadError.push(callback);
    }

    onImportClicked(callback) {
        this.#callbacks.onImportClicked.push(callback);
    }

    show({isReadOnly} = {isReadOnly: false}) {
        this.#html.show({isReadOnly});
    }

    shake() {
        this.#html.shake();
    }

    /**
     * @param {string} url
     * @param {boolean} isReadOnly
     * @returns {Promise<void>}
     */
    async loadStructure(url, isReadOnly = false) {
        const svg = await this.#structureSvgLoader.getByUrlPath(url);
        this.#html.showStructure(svg, isReadOnly);
    }

    setDisabled(isDisabled) {
        this.#disabled = isDisabled;
        this.#html.setPointerEventsDisabled(isDisabled);
    }

    #setEventListeners() {
        this.#html.onFileAttached(this.#onFileDropOrPaste.bind(this));
        this.#fileDropAreaCmp.onDrop(this.#onFileDropOrPaste.bind(this));
        this.#clipboardPasteEventHandler.onPaste(this.#onFileDropOrPaste.bind(this));

        this.#fileDropAreaCmp.onDragEnter(() => this.#html.structureFadeOut());
        this.#fileDropAreaCmp.onDragLeave(() => this.#html.structureFadeIn());

        this.#html.onZoomInClicked(this.#onZoomInClicked.bind(this));
        this.#html.onImportClicked(() => this.#callbacks.onImportClicked.forEach(cb => cb()));
    }

    async #onFileDropOrPaste(content) {
        this.#callbacks.onBeforeProcessingContentToUpload.forEach(callback => callback());
        if (this.#disabled)
            return;
        if (content instanceof FileList) // multiple files uploaded
            content = content.item(0); // only first file will be uploaded
        await this.#processUploadedContent(content);
    }

    #onZoomInClicked(svgXml) {
        this.#zoomedStructurePopup.showHtml(`<main class="dialog__main-content">${svgXml}</main>`);
    }

    /**
     * @param fileOrString
     * @returns {Promise<StructureMetaList|undefined>}
     */
    async #processUploadedContent(fileOrString) {
        try {
            this.#callbacks.onStructureUploadStarted.forEach(callback => callback());
            const structures = await this.#substanceRepository.createStructure(fileOrString);
            this.#callbacks.onStructureUploaded.forEach(callback => callback(structures));
        } catch (e) {
            this.#callbacks.onStructureUploadError.forEach(callback => callback(e));
        }
    }
}